import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from '@angular/core';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {NgTemplateOutlet} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {Autentiseringsmetode, BrukerId, BrukerModell, BrukerRessursClient, GruppeMedlemModell, SlåSammenBrukereSpesifikasjon, SlåSammenBrukereValideringsfeil, ValideringsresultatModell} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {lagrerSignal} from 'felles-ng2/tjenester/LagrerIndikator';
import {VisSpinnerDirektiv} from 'web-ng2/delt/direktiver/button--visSpinner/button--visSpinner';

export interface KobleBrukereDialogRespons {
    koblet: boolean;
    slettesBrukerId?: BrukerId;
    beholdesBrukerId?: BrukerId;
}

@Component({
    selector: 'koble-brukere-dialog',
    templateUrl: 'koble-brukere-dialog.html',
    styleUrl: 'koble-brukere-dialog.scss',
    host: {'class': 'e2e-koblebrukerdialog'},
    imports: [ReactiveFormsModule, MatDialogModule, MatButton, MatFormField, MatInput, MatLabel, NgTemplateOutlet, TranslateModule, VisSpinnerDirektiv],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KobleBrukereDialogKomponent {
    static vis(dialog: MatDialog, brukerId: BrukerId): MatDialogRef<KobleBrukereDialogKomponent, KobleBrukereDialogRespons> {
        return dialog.open(KobleBrukereDialogKomponent, {data: {brukerId} as DialogParametre, disableClose: true, ariaLabelledBy: 'koble-brukere-dialog-tittel', maxWidth: '800px'});
    }

    readonly brukernavn = new FormControl('', {nonNullable: true, validators: [Validators.minLength(2)]});
    readonly bruker = signal<BrukerModell | null>(null);
    readonly medlemskap = signal<GruppeMedlemModell[]>([]);
    readonly brukerSomSlettes = signal<BrukerModell | null>(null);
    readonly medlemskapSomSlettes = signal<GruppeMedlemModell[]>([]);
    readonly ikkeFunnet = signal(false);
    readonly valideringsfeil = signal<ValideringsresultatModell<SlåSammenBrukereValideringsfeil> | null>(null);
    readonly lagrer = lagrerSignal();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: KobleBrukereDialogAvhengigheter,
                private dialogRef: MatDialogRef<KobleBrukereDialogKomponent, KobleBrukereDialogRespons>) {
        this.lastBruker(parametre.brukerId);
    }

    async sokEtterBruker() {
        if (this.brukernavn.invalid) return;
        this.ikkeFunnet.set(false);
        this.brukerSomSlettes.set(null);
        try {
            const bruker = await this.avhengigheter.finnBruker(this.brukernavn.value);
            this.brukerSomSlettes.set(bruker);
            this.medlemskapSomSlettes.set(await this.avhengigheter.medlemskap(bruker.id));
        } catch (feil: any) {
            if (feil.status === 404) this.ikkeFunnet.set(true);
            else throw feil;
        }
    }

    async koble() {
        const beholdes = this.bruker();
        const slettes = this.brukerSomSlettes();
        if (beholdes === null || slettes === null) return;

        await this.lagrer.utfør(async () => {
            const resultat = await this.avhengigheter.koble({beholdesId: beholdes.id, slettesId: slettes.id});
            if (resultat.harFeil) {
                this.valideringsfeil.set(resultat);
                return;
            }
            this.dialogRef.close({koblet: true, beholdesBrukerId: beholdes.id, slettesBrukerId: slettes.id});
        });
    }

    avbryt() {
        this.dialogRef.close({koblet: false});
    }

    autentiseringsmetoder(bruker: BrukerModell): string[] {
        return bruker.autentiseringsmetoder.map(autentiseringsmetodeTekst);
    }

    private async lastBruker(brukerId: BrukerId) {
        const bruker = await this.avhengigheter.hent(brukerId);
        this.bruker.set(bruker);
        this.medlemskap.set(await this.avhengigheter.medlemskap(bruker.id));
    }
}

@Injectable({providedIn: 'root'})
export class KobleBrukereDialogAvhengigheter {
    constructor(private ressurs: BrukerRessursClient) {}

    hent(id: BrukerId): Promise<BrukerModell> { return this.ressurs.hentBruker(id).somPromise(); }
    finnBruker(brukernavn: string): Promise<BrukerModell> { return this.ressurs.finnBrukerMedBrukernavn(brukernavn).somPromise(); }
    medlemskap(id: BrukerId): Promise<GruppeMedlemModell[]> { return this.ressurs.brukersGrupper(id).somPromise(); }
    koble(spesifikasjon: SlåSammenBrukereSpesifikasjon): Promise<ValideringsresultatModell<SlåSammenBrukereValideringsfeil>> { return this.ressurs.kobleBrukere(spesifikasjon).somPromise(); }
}

interface DialogParametre { brukerId: BrukerId; }

function autentiseringsmetodeTekst(metode: Autentiseringsmetode): string {
    switch (metode) {
        case Autentiseringsmetode.passord: return 'Passord';
        case Autentiseringsmetode.totpOtp: return 'Kodegenerator';
        case Autentiseringsmetode.smsOtp: return 'Sms';
        case Autentiseringsmetode.criiptoOpenId: return 'E-Id';
    }
}
